import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form } from 'react-bootstrap';
import { SECTION_LABELS, Skill } from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { Button } from '@Components/Button';
import { Design, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { setAdminSkillEnabled } from './google-ads-audit-admin-api';

interface SkillRowProps {
  skill: Skill;
  onOpen: (id: string) => void;
}

const SkillRow = ({ skill, onOpen }: SkillRowProps) => {
  const queryClient = useQueryClient();
  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => setAdminSkillEnabled(skill.id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['google-ads-audit-admin-skills'] })
  });

  return (
    <div className='google-ads-audit-admin-skill-row'>
      <InteractiveDiv
        className='google-ads-audit-admin-skill-row-main'
        onClick={() => onOpen(skill.id)}
      >
        <span className='google-ads-audit-account-name'>{skill.label}</span>
        <span className='google-ads-audit-account-id'>{skill.key}</span>
        <span className='google-ads-audit-account-id'>{SECTION_LABELS[skill.sectionId]}</span>
        <span className='google-ads-audit-admin-skill-type-badge'>{skill.skillType}</span>
        <span className='google-ads-audit-account-id'>v{skill.currentVersion}</span>
      </InteractiveDiv>
      <Button
        design={skill.enabled ? Design.BRAND : Design.NEUTRAL}
        size={Size.SM}
        onClick={() => toggleMutation.mutate(!skill.enabled)}
        loading={toggleMutation.isPending}
      >
        {skill.enabled ? 'Enabled' : 'Disabled'}
      </Button>
    </div>
  );
};

interface AdminSkillsListProps {
  skills: Skill[];
  initialSectionId?: string;
  onOpenSkill: (id: string) => void;
}

export const AdminSkillsList = ({
  skills,
  initialSectionId,
  onOpenSkill
}: AdminSkillsListProps) => {
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState(initialSectionId ?? '');
  const [typeFilter, setTypeFilter] = useState('');

  const filtered = useMemo(
    () =>
      skills.filter(skill => {
        if (sectionFilter && skill.sectionId !== sectionFilter) return false;
        if (typeFilter && skill.skillType !== typeFilter) return false;
        const q = search.trim().toLowerCase();
        if (q && !skill.label.toLowerCase().includes(q) && !skill.key.toLowerCase().includes(q)) {
          return false;
        }
        return true;
      }),
    [skills, sectionFilter, typeFilter, search]
  );

  const sections = [...new Set(skills.map(s => s.sectionId))];

  return (
    <div className='google-ads-audit-admin-skills-list'>
      <div className='google-ads-audit-form-row'>
        <Form.Control
          placeholder='Search by label or key'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Form.Select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}>
          <option value=''>All sections</option>
          {sections.map(sectionId => (
            <option key={sectionId} value={sectionId}>
              {SECTION_LABELS[sectionId]}
            </option>
          ))}
        </Form.Select>
        <Form.Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value=''>All types</option>
          <option value='agent'>Agent</option>
          <option value='rule'>Rule</option>
        </Form.Select>
      </div>
      {filtered.map(skill => (
        <SkillRow key={skill.id} skill={skill} onOpen={onOpenSkill} />
      ))}
    </div>
  );
};
